import { Box, Card, CardHeader, CircularProgress, Divider, Grid, MenuItem, Select, Stack, 
  Typography, Button, FormHelperText, FormControl, Chip } from "@mui/material";
import { push } from "connected-react-router";
import { ReduxState, crudGetList, useDeleteWithConfirmController, useRecordContext, useSaveContext } from "ra-core";
import { FC, ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHasPermit } from "~/authProvider/useHasPermit";
import { Content } from "~/components/content/Content";
import Confirm from "~/components/widgets/Confirm";
import { EDIT_DATA_CLASSIFICATION_ENCRYPTION } from "~/constants";
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers";
import { useSimpleSaveAction } from "~/controller/SaveContextProvider";
import { RecPatch } from "~/dataProvider/tools";
import { FileEncryptionTemplateDto, FileTemplateKind } from "~/gen/api/encrypt";
import { OdResourcePageProps } from "~/types/resource";
import { disabledColor } from "~/utils/a11y";
import { decodeHashid } from "~/utils/hashid";
import { useUserTimeFormat } from "~/utils/userTimeFormat";
import { useOptionsData, useMappingEditParams } from "./FileTemplateDetail.hooks";
import JsonMappingViewer from "./FileTemplateDetail.JsonMappingViewer";
import { EDIDataFileMappingViewer, TabularDataFileMappingViewer } from "./FileTemplateDetail.TabularViewer";
import CopyFileTemplateMapping from "~/components/obfuscation/CopyFileTemplateMapping";
import { pages, sorts } from "~/utils/paramsWithGetMainList";
import CreateOrEdit from "~/controller/CreateOrEdit";
import { crudGetOneEx } from "~/actions/crudFetchEx";


const resource = 'file-encrypt-template'

const FileTemplateDetail: FC<OdResourcePageProps&{id:string}> = (props) => {
  const { id: hId, ...rest } = props
  const id = decodeHashid(hId || '') 
  const handlers = useDefaultSavedHandlers("Edit File Template Mappings")

  const dispatch = useDispatch()
  
  const stub = useRef<any>(null)
  const record = useSelector<ReduxState, FileEncryptionTemplateDto&RecPatch>(state => {
    const resData = state.admin.resources[resource]
    if (stub.current === null && !resData.list.loadedOnce) {
      stub.current = -1
      setTimeout(() => 
        dispatch(crudGetList(resource, pages(), sorts(), {})))
    }
    return resData.data[id]
  })
  useEffect(() => {
    if (id && record && !(record?.columns?.length) && stub.current !== id) {
      dispatch(crudGetOneEx(resource, id, ""))
      stub.current = id
    }
  }, [id, record])

  return (
    <CreateOrEdit resource={resource} id={id} record={record} basePath="" {...handlers}>
      {record && <FileTemplateDetailBody {...{id}} {...rest} />}
    </CreateOrEdit>
  )
}


const FileTemplateDetailBody: FC<OdResourcePageProps&{id:number}> = ({
  options,
}) => {
  const hasWritePermit = useHasPermit([EDIT_DATA_CLASSIFICATION_ENCRYPTION])
  const save = useSimpleSaveAction()
  const record = useRecordContext<FileEncryptionTemplateDto&RecPatch>()
  const { saving } = useSaveContext()
  const dispatch = useDispatch()
  const refetch = useCallback(() => {
    record && dispatch(crudGetOneEx(resource, record.id, ""))
  }, [record?.id])
  const format = useUserTimeFormat()

  const optionsData = useOptionsData()
  const { keys } = optionsData
  const mappingEditParams = useMappingEditParams({record})
  const { keyLens, gatherEdits, touched, discardAllEdits, version } = mappingEditParams
  const [outdateVersion, setOutdateVer] = useState(-1)

  const saveMappings = () => {
    if (!touched() || saving) { return }
    const payload = gatherEdits()

    save(payload, {
      onSuccess: () => {
        refetch && refetch()
        setOutdateVer(version)
      }
    })
  }

  const delHandlers = useDefaultSavedHandlers("Delete File Template")
  delHandlers.onSuccess.merge(useCallback(() => dispatch(push('/encryption/mappings?tab=file')), [dispatch]))
  const {
    open: openConfirm,
    loading: deleting,
    handleDelete,
    handleDialogClose,
    handleDialogOpen,
  } = useDeleteWithConfirmController({
    resource, basePath: '',
    record,
    ...delHandlers,
  })

  return (
    <Content 
      pageName="File Encryption Template" 
      resourceName="Template Detail" 
      breadcrumbs={[...(options?.breadcrumbs || []), { label: 'Encryption Mappings', path: '/encryption/mappings?tab=file' }]}
      mainActions={
        hasWritePermit &&
        <Button color="error" variant="outlined"
          onClick={handleDialogOpen}
        >
          Delete
        </Button>
      }
    >
      {!(record?.columns?.length) ?
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box> :
        <Grid container spacing={4}
          {...(!hasWritePermit && { sx: {
            '& input.Mui-disabled, & div.Mui-disabled': disabledColor
          }})}
        >
          <Grid item xs={12} >
            <Card>
              <Box sx={{ my: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, mx: 2 }}>
                  <Typography color="textPrimary" variant="h6">Template</Typography>
                  <Typography variant="body1" sx={{ textTransform: 'uppercase' }} >{record.name}</Typography>
                  <Typography variant="caption" component="i"
                    sx={{ 
                      color: theme => theme.palette.mode === 'light' ? '#757575' : '#9e9e9e',
                      display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                      my: -1,
                    }} 
                  >{
                      record.kind === FileTemplateKind.CSV        ? ['CSV File', chipMap[record.delimitingChar ?? '']] 
                    : record.kind === FileTemplateKind.FixedWidth ? 'Fixed Width File' 
                    : record.kind === FileTemplateKind.JsonSample ? 'Json File' 
                    : record.kind === FileTemplateKind.EDI        ? 'EDI File' 
                    : ''
                  }</Typography>
                  <Typography sx={{ color: theme => theme.palette.mode === 'light' ? '#757575' : '#9e9e9e'}} variant="caption" component="i">
                    Upload at: {format(record.uploadTime || 0, "PPp")}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" sx={{ mt: 2, mx: 2 }}>
                  <Typography color="textPrimary" variant="h6">Encryption Key</Typography>
                  <Box sx={{ width: 480, mx: 4 }}>
                    <FormControl fullWidth error={Boolean(touched() && !keyLens.getKeyId())} >
                      <Select
                        fullWidth
                        disabled={!hasWritePermit}
                        name="option"
                        onChange={(e) => keyLens.setKeyId(+e.target.value)}
                        size="small"
                        value={keyLens.getKeyId()}
                        variant="outlined"
                      >
                        {keys.map(k => (
                          <MenuItem key={k.id} value={k.id} >
                            <Typography variant="body1" sx={{ maxWidth: 400, wordBreak: 'keep-all', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', }}>
                              {k.name}
                            </Typography>
                          </MenuItem>
                        ))}
                      </Select>
                      {touched() && !keyLens.getKeyId() && 
                        <FormHelperText >Encryption Key is required.</FormHelperText>}
                    </FormControl>
                  </Box>
                  <CopyFileTemplateMapping />
                </Stack>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} >
            <Card>
              <CardHeader 
                title="Mappings" 
                action={
                  hasWritePermit && touched() &&
                  <Stack direction="row" spacing={2}>
                    <Button
                      disabled={saving || outdateVersion === version}
                      variant="outlined"
                      onClick={discardAllEdits}
                    >
                      Cancel
                    </Button>
                    <Button
                      disabled={saving || outdateVersion === version || !keyLens.getKeyId()}
                      variant="contained"
                      onClick={() => saveMappings()}
                    >
                      Save
                    </Button>
                  </Stack> 
                }
              />
              <Divider />
              {
                record.kind === FileTemplateKind.JsonSample ? <JsonMappingViewer record={record} optionsData={optionsData} mappingEditParams={mappingEditParams} />
              : record.kind === FileTemplateKind.EDI        ? <EDIDataFileMappingViewer record={record} optionsData={optionsData} mappingEditParams={mappingEditParams} />
              : <TabularDataFileMappingViewer record={record} optionsData={optionsData} mappingEditParams={mappingEditParams} />
              }
            </Card>
          </Grid>
        </Grid>
      }
      {hasWritePermit &&
        <Confirm
          isOpen={openConfirm}
          title={"Delete File Template"}
          loading={deleting}
          onConfirm={handleDelete}
          onClose={(e:any) => {handleDialogClose(e)}}
          confirm="Confirm"
          cancel="Cancel"
          content={(
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ ml: 1 }}
                >
                  Please confirm whether to proceed with the deletion of this
                  "{(record?.name ?? '').toUpperCase()}"
                  File Template.
                </Typography>
              </Box>
            </Box>
          )}
        />}
    </Content>
  )
}

export default FileTemplateDetail;

const chipMap : Record<string, ReactElement> = {
  ',':  <Chip size="small" label={<><Typography variant="body1" component="span" sx={{fontWeight:700}}>,</Typography></>} />,
  ';':  <Chip size="small" label={<><Typography variant="body1" component="span" sx={{fontWeight:700}}>;</Typography></>} />,
  '\t': <Chip size="small" label={<>tab ( <Typography variant="body1" component="span" sx={{fontWeight:700}}>\t</Typography> )</>} />,
}
